<template>
  <div class="obituary-comments-container">
    <div class="view-tabs" v-if="hasTimelineImages">
      <button
        class="view-tabs__tab"
        type="button"
        :class="{ 'view-tabs__tab--active': activeView === 'masonry' }"
        :disabled="activeView === 'masonry'"
        @click="() => setActiveView('masonry')"
        v-text="texts.viewMasonry"
      />
      <button
        class="view-tabs__tab"
        type="button"
        :class="{ 'view-tabs__tab--active': activeView === 'timeline' }"
        :disabled="activeView === 'timeline'"
        @click="() => setActiveView('timeline')"
        v-text="texts.viewTimeline"
      />
    </div>

    <loader
      position="whole-page"
      v-if="isActionInModal"
      :high-z-index="true"
      :loading="true"
    />

    <div
      class="obituary-comments__intro"
      :class="{ 'obituary-comments__intro--life': showLifePoster }"
    >
      <div class="obituary-comments__intro-text">
        <h2
          class="obituary__section-heading obituary__section-heading--comments"
        >
          {{ dynamicSectionHeading }}
        </h2>

        <p class="obituary-comments__description" v-text="sectionDescription" />

        <create-comment
          :active-view="activeView"
          :no-donations="noDonations"
          :order-id="orderId"
        />
      </div>

      <LifeStoryPoster
        class="obituary-comments__life-poster"
        v-if="showLifePoster"
        @play="playLife()"
      />
    </div>

    <div
      class="obituary-comments__sort"
      v-if="activeView === 'masonry' && greetings.length > 1"
    >
      <label class="obituary-comments__sort-label" for="obituary-comments-sort">
        {{ texts.sortLabel }}:
      </label>
      <select
        class="obituary-comments__sort-select"
        id="obituary-comments-sort"
        v-model="sortBy"
      >
        <option
          v-for="option in sortOptions"
          :key="option.value"
          :value="option.value"
          v-text="option.label"
        />
      </select>
    </div>

    <div class="obituary-comments-list">
      <template v-if="activeView === 'masonry'">
        <div
          class="masonry-skeleton"
          v-if="!finalCommentsList.length || isMasonryUpdating"
        >
          <div class="masonry-skeleton__card" v-for="n in 6" :key="n" />
        </div>

        <masonry-grid
          v-if="finalCommentsList.length"
          :list="finalCommentsList"
          @updated="onMasonryUpdated"
        >
          <single-comment
            class="grid-item"
            v-for="comment in finalCommentsList"
            :key="comment.id"
            :comment="comment"
            :deceased-name="deceasedName"
            :memorial-fund="memorialFund"
            :possessive-name="possessiveName"
          />
        </masonry-grid>
      </template>

      <template v-if="activeView === 'timeline'">
        <div class="obituary-timeline">
          <TimelineYearGroup
            v-for="group in groupedMediaImagesByYear"
            :key="group.year"
            :images="group.images"
            :playable="lifeStory"
            :year="group.year"
            @image-click="(index) => openTimelineMedia(group, index)"
            @play="playLife(Number(group.year))"
          />
        </div>
      </template>

      <div
        class="load-more-btn-container"
        v-if="activeView === 'masonry' && hasMorePosts"
      >
        <button
          class="btn btn-cta"
          type="button"
          @click.stop.prevent="loadMore"
          v-text="texts.loadMore"
        />
      </div>
    </div>

    <comment-view-modal />

    <threaded-comments-modal v-if="!isMobile" :obituary-id="obituaryId" />

    <threaded-comments-mobile-modal v-else :obituary-id="obituaryId" />
  </div>
</template>

<script>
  import Loader from "Components/loader/Loader.vue";
  import MasonryGrid from "Components/MasonryGrid.vue";
  import { useIsMobile } from "Main/composables/useIsMobile";
  import { sprintf } from "sprintf-js";
  import { mapActions, mapGetters, mapState } from "vuex";
  import {
    lifeStoryHash,
    lifeStoryOnPage,
    openViewer,
    quickContent,
  } from "../composables/useSlideshow";
  import {
    COMMENT_SORT_DATE_NEWEST,
    COMMENT_SORT_DATE_OLDEST,
    COMMENT_SORT_NAME_ASC,
    COMMENT_SORT_NAME_DESC,
    COMMENT_SORT_RELEVANCE,
    sortComments,
  } from "../helpers";
  import { arrivalSource } from "../slideshow/share";
  import {
    formatDecade,
    groupMediaImagesByYear,
    hasEnoughTimelineImages,
  } from "../timeline-helpers";
  import CommentViewModal from "./CommentViewModal.vue";
  import CreateComment from "./CreateComment/index.vue";
  import SingleComment from "./SingleComment.vue";
  import LifeStoryPoster from "./Slideshow/LifeStoryPoster.vue";
  import ThreadedCommentsMobileModal from "./ThreadedComments/MobileModal.vue";
  import ThreadedCommentsModal from "./ThreadedComments/Modal.vue";
  import TimelineYearGroup from "./TimelineYearGroup.vue";

  const MOBILE_LOAD_MORE_NUMBER = 12;

  export default {
    components: {
      CommentViewModal,
      CreateComment,
      LifeStoryPoster,
      Loader,
      MasonryGrid,
      SingleComment,
      ThreadedCommentsMobileModal,
      ThreadedCommentsModal,
      TimelineYearGroup,
    },

    computed: {
      ...mapState("comments", ["commentsList", "isLoading", "isActionInModal"]),
      ...mapGetters("comments", ["getActiveComments"]),

      dynamicSectionHeading() {
        return this.activeView === "timeline"
          ? sprintf(this.texts.timelineHeading, this.possessiveName)
          : sprintf(this.texts.condolencesHeading, this.deceasedName);
      },

      finalCommentsList() {
        return this.isMobile
          ? this.sortedComments.slice(0, this.shownCommentsCount)
          : this.sortedComments;
      },

      // Photos the family added for the timeline only are not greetings.
      greetings() {
        return this.getActiveComments.filter(
          ({ timelineOnly }) => !timelineOnly
        );
      },

      groupedMediaImagesByYear() {
        return groupMediaImagesByYear(this.getActiveComments);
      },

      hasMorePosts() {
        return this.isMobile
          ? this.sortedComments.length > this.finalCommentsList.length
          : false;
      },

      hasTimelineImages() {
        return (
          this.timelineEnabled &&
          this.timelineShown &&
          hasEnoughTimelineImages(this.groupedMediaImagesByYear)
        );
      },

      sectionDescription() {
        return this.activeView === "timeline"
          ? sprintf(this.texts.timelineDescription, this.possessiveName)
          : sprintf(this.texts.condolencesDescription, this.deceasedName);
      },

      showLifePoster() {
        return this.activeView === "timeline" && this.lifeStory;
      },

      sortedComments() {
        // The html lang attribute carries the site locale (e.g. sv-SE, da-DK)
        // so name sorting collates correctly per market.
        return sortComments(
          this.greetings,
          this.sortBy,
          document.documentElement.lang || "sv"
        );
      },

      sortOptions() {
        return [
          { label: this.texts.sortRelevance, value: COMMENT_SORT_RELEVANCE },
          { label: this.texts.sortDateNewest, value: COMMENT_SORT_DATE_NEWEST },
          { label: this.texts.sortDateOldest, value: COMMENT_SORT_DATE_OLDEST },
          { label: this.texts.sortNameAsc, value: COMMENT_SORT_NAME_ASC },
          { label: this.texts.sortNameDesc, value: COMMENT_SORT_NAME_DESC },
        ];
      },
    },

    data() {
      const {
        orderId,
        obituaryId,
        deceasedName,
        possessiveName,
        memorialFund,
        noDonations,
        sectionHeading,
        timelineEnabled,
      } = this.$mountEl.dataset;

      return {
        activeView: "masonry",
        deceasedName,
        isMasonryUpdating: true,
        lifeStory: lifeStoryOnPage(),
        memorialFund: JSON.parse(memorialFund),
        noDonations: noDonations === "true",
        obituaryId,
        orderId,
        possessiveName,
        sectionHeading: sectionHeading || "",
        shownCommentsCount: MOBILE_LOAD_MORE_NUMBER,
        sortBy: COMMENT_SORT_RELEVANCE,
        texts: lavendlaObituary.common,
        timelineEnabled: timelineEnabled === "true",
        timelineShown: quickContent()?.family?.timeline?.show !== false,
      };
    },

    methods: {
      ...mapActions("comments", ["getObituaryComments"]),
      ...mapActions({ openMediaModal: "modal/openMedia" }),

      loadMore() {
        this.shownCommentsCount += MOBILE_LOAD_MORE_NUMBER;
      },

      onMasonryUpdated() {
        this.isMasonryUpdating = false;
      },

      // The timeline lightbox is scoped to the decade group that was clicked,
      // so it spans several comments and needs the year/caption per image.
      openTimelineMedia(group, index) {
        this.openMediaModal({
          heading: formatDecade(group.year),
          items: group.images.map(({ src, text, yearTaken }) => ({
            src,
            text,
            yearTaken,
          })),
          startIndex: index,
        });
      },

      playLife(decade = null) {
        openViewer({
          chapter: decade,
          kind: "life",
          source: decade === null ? "timeline_poster" : "timeline_decade",
        });
      },

      setActiveView(view) {
        this.activeView = view;

        if (view === "masonry") {
          this.isMasonryUpdating = true;
        }
      },
    },

    mounted() {
      this.getObituaryComments(this.obituaryId);
      // …#tidslinje plays the life story; …-dela (the QR code on a computer) opens sharing.
      const hash = `#${lifeStoryHash()}`;
      const share = window.location.hash === `${hash}-dela`;
      if (this.lifeStory && (share || window.location.hash === hash)) {
        this.setActiveView("timeline");
        this.$nextTick(() =>
          this.$el.scrollIntoView?.({ behavior: "instant", block: "start" })
        );
        openViewer({
          kind: "life",
          share,
          source: arrivalSource() ?? (share ? "qr_handoff" : "link"),
        });
      }
    },
    name: "CommentsList",

    setup() {
      const { isMobile, isTablet } = useIsMobile();
      return { isMobile, isTablet };
    },
  };
</script>
